import { useQuery } from '@tanstack/react-query';
import { Icon } from '@iconify/react';
import { useTeamPreferences } from '../../hooks/useTeamPreferences';
import { useAuth } from '../../contexts/AuthContext';
import { backendProvider } from '../../services/backend';
import CompanyLogoField from './CompanyLogoField';
import BrandPrimaryColourField from './BrandPrimaryColourField';
import { canManageBranding } from '../../utils/brandingAccess';

export default function BrandingSettingsTab() {
  const { currentUser } = useAuth();
  const { data: teamPreferences, isLoading, isError } = useTeamPreferences();
  // Branding is editable only for PARTNER teams (team pref `partner` = true)
  // whose user carries a teamadmin/admin/superadmin label OR has the owner
  // role in the team membership.  The membership-roles query fetches ALL
  // memberships (no Query.equal — the Client SDK doesn't support it) and
  // finds the caller by userId.
  const partner = teamPreferences?.partner;

  const { data: membershipRoles = [] } = useQuery({
    queryKey: ['branding', 'access', currentUser?.uid || ''],
    queryFn: () => backendProvider.auth.getTeamMembershipRoles(),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(currentUser?.uid),
  });

  const canEditBranding = canManageBranding(membershipRoles, currentUser?.labels, { partner });
  // NOTE: There is deliberately NO "reset branding" action. Appwrite's
  // teams.updatePrefs REPLACES the whole prefs object, and a reset that only
  // sends the two branding keys wipes every other team preference (modules,
  // partner flag, seller_ids, integrations…). See team-branding-manager.

  // Show the form immediately — data is cached from the prefetch at boot.
  // The loading gate was removed because it caused a visible delay every time
  // the user switched to the Branding tab.

  if (isError) {
    return (
      <div role="alert" className="flex items-start gap-3 rounded-2xl border border-rose-400/25 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
        <Icon icon="bx:error-circle" className="mt-0.5 h-5 w-5 shrink-0" />
        Company branding could not be loaded. Close Settings and try again.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {!canEditBranding && (
        <div className="flex items-start gap-2 border-t border-white/[0.06] pt-3 text-gray-400">
          <Icon icon="bx:lock" className="mt-0.5 h-4 w-4 shrink-0 text-white/35" />
          <p className="text-[11px] leading-5 text-gray-500">
            Branding is view-only for your account. Logo and colour personalization is available on partner plans — contact the Brainiacs team to enable it.
          </p>
        </div>
      )}
      <CompanyLogoField
        companyName={teamPreferences?.team_name}
        logoUrl={teamPreferences?.logo_url}
        isDefaultLogo={teamPreferences?.company_logo_is_default}
        canEdit={canEditBranding}
      />
      <BrandPrimaryColourField colour={teamPreferences?.brand_primary_colour} canEdit={canEditBranding} />
    </div>
  );
}
